import React, { useState, useEffect } from "react";
import {
  X,
  FileText,
  Trash2,
  Link,
  Unlink,
  DollarSign,
  Calendar,
  Search,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

const CURRENCY_SYMBOL = { ILS: "₪", USD: "$", EUR: "€", GBP: "£" };
const getSymbol = (code) => CURRENCY_SYMBOL[code] || "₪";

const parseAmount = (str) => {
  if (!str) return 0;
  const n = parseFloat(String(str).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
};

export default function BossPlaceInvoicesModal({
  visible,
  onClose,
  placeId,
  placeName,
  onInvoicesChange,
}) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [linking, setLinking] = useState({});
  const [deleting, setDeleting] = useState({});
  const [error, setError] = useState(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id;
  const API = MyDomain.endsWith("/") ? MyDomain.slice(0, -1) : MyDomain;

  useEffect(() => {
    if (visible && userId) loadInvoices();
  }, [visible, userId]);

  const loadInvoices = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/email-invoices/${userId}`);
      if (res.ok) {
        const d = await res.json();
        setInvoices(d.invoices || []);
      }
    } catch (err) {
      setError("Failed to load invoices");
    }
    setLoading(false);
  };

  const toggleLink = async (inv) => {
    const isLinked = inv.placeId === placeId;
    const key = inv.id;
    setLinking((p) => ({ ...p, [key]: true }));
    try {
      const res = await fetch(
        `${API}/api/email-invoices/${userId}/${inv.id}/place`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ placeId: isLinked ? null : placeId }),
        },
      );
      if (res.ok) {
        setInvoices((prev) =>
          prev.map((i) =>
            i.id === key ? { ...i, placeId: isLinked ? null : placeId } : i,
          ),
        );
        if (onInvoicesChange) onInvoicesChange();
      }
    } catch (err) {
      setError("Failed to update invoice");
    }
    setLinking((p) => ({ ...p, [key]: false }));
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm("Delete this invoice?")) return;
    const key = inv.id;
    setDeleting((p) => ({ ...p, [key]: true }));
    try {
      const res = await fetch(`${API}/api/email-invoices/${userId}/${inv.id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setInvoices((prev) => prev.filter((i) => i.id !== key));
        if (onInvoicesChange) onInvoicesChange();
      }
    } catch {
      setError("Failed to delete invoice");
    }
    setDeleting((p) => ({ ...p, [key]: false }));
  };

  if (!visible) return null;

  const linkedInvoices = invoices.filter((i) => i.placeId === placeId);
  const otherInvoices = invoices.filter((i) => i.placeId !== placeId);

  const linkedTotals = {};
  linkedInvoices.forEach((inv) => {
    if (!inv.price) return;
    const code = inv.currency || "ILS";
    const amt = parseAmount(inv.price);
    if (!amt) return;
    if (!linkedTotals[code]) linkedTotals[code] = 0;
    linkedTotals[code] += amt;
  });

  const q = search.toLowerCase();
  const filterInv = (list) =>
    !q
      ? list
      : list.filter(
          (i) =>
            (i.subject || "").toLowerCase().includes(q) ||
            (i.whoSend || "").toLowerCase().includes(q) ||
            (i.invoiceNumber || "").toLowerCase().includes(q),
        );

  const SOURCE_COLOR = {
    gmail: "#be123c",
    outlook: "#1d4ed8",
    manual: "#6d28d9",
  };

  const InvoiceRow = ({ inv, linked }) => {
    const sym = getSymbol(inv.currency || "ILS");
    const amount = parseAmount(inv.price);
    const src = inv.source || "manual";
    // Invoice is linked to a DIFFERENT place (not this one)
    const linkedElsewhere = !linked && inv.placeId && inv.placeId !== placeId;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          background: linked
            ? "#f0fdf4"
            : linkedElsewhere
              ? "#fffbeb"
              : "#f9fafb",
          border: `1px solid ${linked ? "#86efac" : linkedElsewhere ? "#fcd34d" : "#e5e7eb"}`,
          borderRadius: 10,
          marginBottom: 6,
        }}
      >
        {/* Source dot */}
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            background: SOURCE_COLOR[src] || "#6b7280",
          }}
        />

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#111827",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {inv.subject || inv.whoSend || "Invoice"}
          </div>
          <div
            style={{
              fontSize: 10,
              color: "#6b7280",
              marginTop: 2,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {inv.date && <span>📅 {inv.date}</span>}
            {inv.invoiceNumber && <span>#{inv.invoiceNumber}</span>}
            <span
              style={{
                color: SOURCE_COLOR[src],
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              {src}
            </span>
            {/* ✅ Already linked badge */}
            {linkedElsewhere && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  background: "#fef3c7",
                  color: "#b45309",
                  border: "1px solid #fcd34d",
                  borderRadius: 5,
                  padding: "1px 6px",
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                <AlertCircle size={8} />
                Linked elsewhere
              </span>
            )}
            {linked && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  background: "#dcfce7",
                  color: "#15803d",
                  border: "1px solid #86efac",
                  borderRadius: 5,
                  padding: "1px 6px",
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                <CheckCircle size={8} />
                Linked here
              </span>
            )}
          </div>
        </div>

        {/* Amount */}
        {amount > 0 && (
          <div
            style={{
              fontWeight: 700,
              fontSize: 13,
              color: linked
                ? "#059669"
                : linkedElsewhere
                  ? "#b45309"
                  : "#374151",
              flexShrink: 0,
              fontFamily: "monospace",
            }}
          >
            {sym}
            {amount.toLocaleString("he-IL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        )}

        {/* Link / Unlink button */}
        <button
          onClick={() => toggleLink(inv)}
          disabled={!!linking[inv.id]}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "5px 10px",
            borderRadius: 7,
            border: "none",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 600,
            flexShrink: 0,
            background: linked ? "#fef2f2" : "#eff6ff",
            color: linked ? "#dc2626" : "#2563eb",
            opacity: linking[inv.id] ? 0.5 : 1,
          }}
        >
          {linking[inv.id] ? (
            "..."
          ) : linked ? (
            <>
              <Unlink size={11} /> Unlink
            </>
          ) : (
            <>
              <Link size={11} /> Link
            </>
          )}
        </button>

        {/* Delete */}
        <button
          onClick={() => deleteInvoice(inv)}
          disabled={!!deleting[inv.id]}
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            border: "none",
            background: "#fee2e2",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            opacity: deleting[inv.id] ? 0.5 : 1,
          }}
        >
          <Trash2 size={13} color="#ef4444" />
        </button>
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          background: "white",
          width: "100%",
          maxWidth: 640,
          maxHeight: "92vh",
          borderRadius: "24px 24px 0 0",
          display: "flex",
          flexDirection: "column",
          fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "12px 20px 16px",
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <div
            style={{
              width: 36,
              height: 4,
              background: "#e5e7eb",
              borderRadius: 2,
              margin: "0 auto 12px",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "linear-gradient(135deg,#a78bfa,#7c3aed)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FileText size={20} color="white" />
              </div>
              <div>
                <div
                  style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}
                >
                  {t("invoices")}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {placeName}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "none",
                background: "#f3f4f6",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={18} color="#374151" />
            </button>
          </div>

          {/* Summary — totals by currency */}
          {Object.keys(linkedTotals).length > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 10,
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "#059669",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                {t("total")}
              </div>
              {Object.entries(linkedTotals).map(([code, sum]) => (
                <div
                  key={code}
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: "#059669",
                    fontFamily: "monospace",
                  }}
                >
                  {getSymbol(code)}
                  {sum.toLocaleString("he-IL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      marginLeft: 4,
                      color: "#16a34a",
                    }}
                  >
                    {code}
                  </span>
                </div>
              ))}
              <div
                style={{ fontSize: 11, color: "#16a34a", marginLeft: "auto" }}
              >
                {linkedInvoices.length} invoice
                {linkedInvoices.length !== 1 ? "s" : ""}
              </div>
            </div>
          )}
        </div>

        {/* Search */}
        <div style={{ padding: "10px 20px 0" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "8px 12px",
            }}
          >
            <Search size={14} color="#9ca3af" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("invoices")}
              style={{
                flex: 1,
                border: "none",
                background: "transparent",
                fontSize: 13,
                outline: "none",
                color: "#111827",
              }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "#9ca3af",
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px 24px" }}>
          {error && (
            <div
              style={{
                padding: "10px 14px",
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: 8,
                fontSize: 12,
                color: "#be123c",
                marginBottom: 12,
              }}
            >
              ⚠ {error}
            </div>
          )}

          {loading ? (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 40 }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  border: "3px solid #e5e7eb",
                  borderTopColor: "#7c3aed",
                  borderRadius: "50%",
                  animation: "spin 0.7s linear infinite",
                }}
              />
            </div>
          ) : (
            <>
              {/* Linked invoices */}
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#059669",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <CheckCircle size={13} /> {t("invoices")} — {placeName} (
                  {linkedInvoices.length})
                </div>
                {filterInv(linkedInvoices).length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px 16px",
                      background: "#f9fafb",
                      borderRadius: 10,
                      border: "1px dashed #e5e7eb",
                    }}
                  >
                    <FileText
                      size={24}
                      color="#d1d5db"
                      style={{ margin: "0 auto 8px" }}
                    />
                    <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
                      {search
                        ? t("noData")
                        : t("noInvoicesFound")}
                    </p>
                  </div>
                ) : (
                  filterInv(linkedInvoices).map((inv) => (
                    <InvoiceRow key={inv.id} inv={inv} linked={true} />
                  ))
                )}
              </div>

              {/* Other invoices */}
              {filterInv(otherInvoices).length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      marginBottom: 8,
                    }}
                  >
                    {t("invoices")} ({filterInv(otherInvoices).length})
                  </div>
                  {filterInv(otherInvoices).map((inv) => (
                    <InvoiceRow key={inv.id} inv={inv} linked={false} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
